import { useMemo, useState } from 'react'
import { useI18n } from '../i18n/useI18n'
import { useAsync } from '../lib/useAsync'
import {
  REPORTS, monthRange, yearRange, groupByPeriod, sumColumn,
  type ReportDef, type ReportResult, type ReportRange,
} from '../lib/reports'
import { downloadCsv, printReport, type ExportColumn } from '../lib/export'
import {
  defaultPeriodForYear, formatMoney, formatPeriod, yearOfPeriod,
} from '../lib/format'
import { useSelectedYear } from '../year/useYear'
import { Loading, ErrorState, EmptyState } from '../components/States'

function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

export function ReportsScreen() {
  const { t } = useI18n()
  const { year } = useSelectedYear()
  const [chosen, setChosen] = useState<ReportDef | null>(null)
  // L'annee vient du cadre de l'application : on ne la choisit pas
  // ici. Seul le decoupage reste au choix, le mois ou l'annee entiere.
  const [period, setPeriod] = useState(() => defaultPeriodForYear(year))
  const [mode, setMode] = useState<'month' | 'year'>('year')

  if (!chosen) {
    return (
      <div className="screen">
        <h1 className="screen__title">{t('reports_title')}</h1>
        <p className="muted">{t('reports_intro')}</p>

        <ul className="list">
          {REPORTS.map((r) => (
            <li key={r.key}>
              <button
                type="button"
                className="list__item list__item--tappable"
                onClick={() => setChosen(r)}
              >
                <span className="list__main">
                  <span className="list__name">{t(r.titleKey)}</span>
                  <span className="list__meta">{t(r.descriptionKey)}</span>
                  {r.monthly && (
                    <span className="list__note">{t('report_month_or_year')}</span>
                  )}
                </span>
                <span className="list__side">→</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <ReportView
      def={chosen}
      range={mode === 'year' ? yearRange(year) : monthRange(period)}
      mode={mode}
      onModeChange={setMode}
      period={period}
      onPeriodChange={setPeriod}
      year={year}
      onBack={() => setChosen(null)}
    />
  )
}

function ReportView({
  def, range, mode, onModeChange,
  period, onPeriodChange, year, onBack,
}: {
  def: ReportDef
  range: ReportRange
  mode: 'month' | 'year'
  onModeChange: (m: 'month' | 'year') => void
  period: string
  onPeriodChange: (p: string) => void
  year: number
  onBack: () => void
}) {
  const { t, lang } = useI18n()
  const { data, loading, error, reload } = useAsync<ReportResult>(
    () => def.load(range, t),
    [def.key, range.mode, range.period, range.year],
  )

  // Regroupement par mois, uniquement sur une annee entiere et pour un
  // rapport dont chaque ligne porte son mois.
  const grouped = mode === 'year' && def.monthly && data?.groupable === true

  /** Colonne « Mois », ajoutee devant les autres en mode annee. */
  const columns = useMemo(() => {
    if (!data) return []
    if (!grouped) return data.columns
    return [
      {
        label: t('report_period'),
        display: (r: Record<string, unknown>) =>
          r.period ? formatPeriod(String(r.period), lang) : '—',
        raw: (r: Record<string, unknown>) => (r.period ? String(r.period) : ''),
      },
      ...data.columns,
    ]
  }, [data, grouped, lang, t])

  /** Les lignes decoupees par mois, dans l'ordre. */
  const groups = useMemo(
    () => (data && grouped ? groupByPeriod(data.rows) : null),
    [data, grouped],
  )

  function sumOf(rows: Record<string, unknown>[], colIndex: number): number | null {
    const col = columns[colIndex]
    if (!col || !col.sum) return null
    return sumColumn(rows, col)
  }

  function exportCsv() {
    if (!data) return
    const cols: ExportColumn<Record<string, unknown>>[] = columns.map((c) => ({
      label: c.label,
      value: c.raw,
    }))
    // Le fichier ne contient que les lignes, sans les sous-totaux :
    // Excel sait les recalculer, et une ligne de total melee aux
    // donnees empeche tout tri ou tableau croise.
    const nom = def.monthly
      ? `${def.key}-${mode === 'year' ? year : range.period}`
      : `${def.key}-${new Date().toISOString().slice(0, 10)}`
    downloadCsv(nom, cols, data.rows)
  }

  const totals = data ? columns.map((_, i) => sumOf(data.rows, i)) : []

  return (
    <div className="screen">
      <button type="button" className="backlink no-print" onClick={onBack}>
        ← {t('reports_title')}
      </button>

      <h1 className="screen__title">{t(def.titleKey)}</h1>

      {def.monthly ? (
        <>
          {/* Mois ou annee entiere. */}
          <div className="scopetoggle no-print" role="group">
            <button
              type="button"
              className={`scopetoggle__btn ${mode === 'month' ? 'is-active' : ''}`}
              aria-pressed={mode === 'month'}
              onClick={() => onModeChange('month')}
            >
              {t('report_scope_month')}
            </button>
            <button
              type="button"
              className={`scopetoggle__btn ${mode === 'year' ? 'is-active' : ''}`}
              aria-pressed={mode === 'year'}
              onClick={() => onModeChange('year')}
            >
              {t('report_scope_year')}
            </button>
          </div>

          <p className="muted">
            {mode === 'year' ? String(year) : formatPeriod(range.period, lang)}
          </p>

          {/* Navigation de mois seulement : l'annee est celle du
              bandeau, on la change la-haut. */}
          {mode === 'month' && (
            <div className="monthnav no-print">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={period.endsWith('-01')}
                onClick={() => {
                  const prev = shiftPeriod(period, -1)
                  if (yearOfPeriod(prev) === year) onPeriodChange(prev)
                }}
              >
                ← {t('previous')}
              </button>
              <span className="monthnav__label">
                {formatPeriod(range.period, lang)}
              </span>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={period.endsWith('-12')}
                onClick={() => {
                  const next = shiftPeriod(period, 1)
                  if (yearOfPeriod(next) === year) onPeriodChange(next)
                }}
              >
                {t('next')} →
              </button>
            </div>
          )}

          {mode === 'year' && (
            <p className="muted">{t('report_year_note', { year })}</p>
          )}
        </>
      ) : (
        <p className="muted">{t('report_all_time')}</p>
      )}

      {data?.note && (
        <div className="banner banner--warn">
          <span>{data.note}</span>
        </div>
      )}

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {data && data.rows.length === 0 && <EmptyState title={t('report_empty')} />}

      {data && data.rows.length > 0 && (
        <>
          <div className="reportbar no-print">
            <button type="button" className="btn btn--ghost btn--small" onClick={exportCsv}>
              {t('report_export_excel')}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={printReport}
            >
              {t('report_export_pdf')}
            </button>
          </div>

          <p className="muted">
            {t('report_rows', { n: data.rows.length })}
            {groups ? ` · ${t('report_months_covered', { n: groups.length })}` : ''}
          </p>

          {/* Un tableau large defile DANS son cadre. La page elle-meme
              ne defile jamais lateralement. */}
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((c, i) => (
                    <th key={i} className={c.align === 'right' ? 'is-right' : ''}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {groups ? (
                // Un corps de tableau par mois, avec son sous-total :
                // sur une annee, un tableau plat de cent lignes ne se
                // lit pas.
                groups.map(([mois, rows]) => (
                  <tbody key={mois} className="table__group">
                    <tr className="table__grouphead">
                      <td colSpan={columns.length}>
                        {formatPeriod(mois, lang)} ·{' '}
                        {t('report_rows', { n: rows.length })}
                      </td>
                    </tr>
                    {rows.map((r, ri) => (
                      <tr key={ri}>
                        {columns.map((c, ci) => (
                          <td key={ci} className={c.align === 'right' ? 'is-right' : ''}>
                            {c.display(r)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="table__subtotal">
                      {columns.map((_, ci) => {
                        const v = sumOf(rows, ci)
                        return (
                          <td key={ci} className={v !== null ? 'is-right' : ''}>
                            {v !== null
                              ? formatMoney(v)
                              : ci === 0
                                ? t('report_subtotal')
                                : ''}
                          </td>
                        )
                      })}
                    </tr>
                  </tbody>
                ))
              ) : (
                <tbody>
                  {data.rows.map((r, ri) => (
                    <tr key={ri}>
                      {columns.map((c, ci) => (
                        <td key={ci} className={c.align === 'right' ? 'is-right' : ''}>
                          {c.display(r)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              )}

              {totals.some((v) => v !== null) && (
                <tfoot>
                  <tr>
                    {totals.map((v, i) => (
                      <td key={i} className={v !== null ? 'is-right' : ''}>
                        {v !== null
                          ? formatMoney(v)
                          : i === 0
                            ? mode === 'year'
                              ? t('report_total_year', { year })
                              : t('report_total')
                            : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  )
}
